import type { Command as CommandPrimitive } from "cmdk"
import type {
	ComponentPropsWithoutRef,
	FC,
	KeyboardEvent,
	PropsWithChildren,
	ReactNode,
	SVGProps,
} from "react"
import { memo, useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import type { ButtonProps, IconType, PopoverProps } from "@productlane/ui"
import { cn } from "@productlane/lib/src/browser"
import { Button, Loader, Popover, Shortcut } from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import { useRole } from "@/lib/useRole"
import { CommandSubmenu } from "./command-submenu"

export interface DataOptionType {
	label: ReactNode
	value: string
	// Consecutive options sharing a group render under one heading.
	group?: string
	// Shorter label for the closed chip when the list row carries extra detail.
	triggerLabel?: ReactNode
	// Opens a side menu instead of selecting; its options go through the same onChange.
	submenu?: DataOptionType[]
}

// Linear's menu: 32px rows of 13px regular text, section labels in line with the row icons.
const ITEM_CLASS = "h-8 py-0 text-sm font-normal text-primary"
const GROUP_CLASS =
	"px-1.5 py-0 [&_[cmdk-group-heading]]:flex [&_[cmdk-group-heading]]:h-[30px] [&_[cmdk-group-heading]]:items-center [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-0 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:tracking-normal [&_[cmdk-group-heading]]:text-secondary"
const CHECK_CLASS = "h-3.5 w-3.5 flex-none text-primary"

export function groupOptions(options: readonly DataOptionType[]) {
	const sections: {
		heading?: string
		items: { option: DataOptionType; index: number }[]
	}[] = []
	options.forEach((option, index) => {
		const last = sections.at(-1)
		if (last && last.heading === option.group) {
			last.items.push({ option, index })
		} else {
			sections.push({ heading: option.group, items: [{ option, index }] })
		}
	})
	return sections
}

interface Props {
	onChange: (value: string | null) => unknown
	className?: string
	align?: PopoverProps["align"]
	data: DataOptionType[] | null | undefined
	selected?: string | number | undefined
	placeholder: ComponentPropsWithoutRef<
		typeof CommandPrimitive.Input
	>["placeholder"]
	resetOption?: string
	resetIcon?: LucideIcon | IconType
	name?: string
	icon?: LucideIcon | IconType
	variant?: ButtonProps["variant"]
	selectedItems?: string[]
	noResultsLabel?: string
	isLoading?: boolean
	popoverClassName?: string
	// Keys that open this picker, shown in the search row as Linear does.
	searchShortcut?: string[]
	onOpenChange?: (open: boolean) => void
	open?: boolean
	tooltip?: ReactNode
	// Linear's 0-9 picking: each digit selects that row while the search is empty.
	numberShortcuts?: boolean
	// Keeps an empty chip in full colour, like the ones that hold a value.
	activePlaceholder?: boolean
}

export function numberShortcutIndex(
	event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey">,
) {
	if (event.metaKey || event.ctrlKey || event.altKey) return undefined
	return /^[0-9]$/.test(event.key) ? Number(event.key) : undefined
}

export const PopoverButton: FC<PropsWithChildren<Props>> = memo(
	({
		variant,
		onChange,
		selected,
		className,
		align,
		data,
		placeholder,
		resetOption,
		resetIcon: ResetIcon,
		name,
		icon: Icon,
		children,
		selectedItems,
		noResultsLabel,
		isLoading,
		popoverClassName,
		searchShortcut,
		onOpenChange: onOpenChangeProp,
		open: openProp,
		tooltip,
		numberShortcuts,
		activePlaceholder,
	}) => {
		const { role } = useRole()
		const [internalOpen, setOpen] = useToggle(false)
		const [search, setSearch] = useState("")
		const [openSubmenu, setOpenSubmenu] = useState<string | null>(null)
		const open = openProp ?? internalOpen
		const onOpenChange = (next: boolean) => {
			setOpen(next)
			if (!next) {
				setSearch("")
				setOpenSubmenu(null)
			}
			onOpenChangeProp?.(next)
		}
		const submenuValues = new Set(
			(data ?? []).filter(({ submenu }) => submenu).map(({ value }) => value),
		)
		const select = (value: string | null) => {
			if (value !== null && submenuValues.has(value)) {
				setOpenSubmenu(value)
				return
			}
			onChange(value)
			onOpenChange(false)
		}
		// The reset row comes first, so it takes 0 like Linear's "No priority".
		const optionValues = [
			...(resetOption ? [null] : []),
			...(data ?? []).map(({ value }) => value),
		]
		const resetOffset = resetOption ? 1 : 0
		const numberHint = (index: number) =>
			numberShortcuts ? (
				<span className="w-2.5 flex-none text-right font-normal tabular-nums text-tertiary">
					{!search && index < 10 ? index : null}
				</span>
			) : null

		const isSelected = (value: string) =>
			selectedItems ? selectedItems.includes(value) : selected === value

		const selectedOption =
			selected || selected === 0
				? data?.find(({ value }) => value === selected)
				: undefined
		const selectedLabel = selectedOption
			? (selectedOption.triggerLabel ?? selectedOption.label)
			: undefined
		const hasChoices = !!data?.length || !!resetOption
		// Treat a selected value with no matching option as unselected, so the trigger falls
		// back to the placeholder instead of rendering blank.
		const showPlaceholder = hasChoices && selectedLabel === undefined
		const isWaiting = !!isLoading && !data?.length
		const triggerLabel = !hasChoices
			? noResultsLabel || "Nothing found"
			: (selectedLabel ?? name ?? (Icon ? "..." : <Loader />))
		const sections = groupOptions(data ?? [])
		// The reset row leads, outside any heading.
		if (resetOption && sections[0]?.heading !== undefined) {
			sections.unshift({ items: [] })
		}
		if (resetOption && sections.length === 0) sections.push({ items: [] })
		const resetItem = (
			<CommandItem className={ITEM_CLASS} onSelect={() => select(null)}>
				<div className="flex w-full items-center gap-2">
					{ResetIcon ? (
						<ResetIcon className="h-4 w-4 shrink-0" />
					) : (
						<X className="h-4 w-4 shrink-0" />
					)}
					<span className="flex-1 truncate">{resetOption}</span>
					{numberHint(0)}
				</div>
			</CommandItem>
		)

		return (
			<Popover
				open={open}
				onOpenChange={onOpenChange}
				className={cn("w-full min-w-max p-0", popoverClassName)}
				align={align}
				triggerTooltip={tooltip}
				content={
					// The popover sizes to its content, so the input stays mounted while
					// loading; a bare spinner would shrink it to a few px.
					<Command className="min-w-[212px]">
						<CommandInput
							showIcon={false}
							className="px-1.5 py-2"
							endAdornment={searchShortcut?.map((key) => (
								<Shortcut key={key}>{key}</Shortcut>
							))}
							autoFocus
							placeholder={placeholder}
							value={search}
							onValueChange={setSearch}
							onKeyDown={(event) => {
								if (!numberShortcuts || search || isWaiting) return
								const index = numberShortcutIndex(event)
								if (index === undefined || index >= optionValues.length) {
									return
								}
								event.preventDefault()
								select(optionValues[index] ?? null)
							}}
						/>
						<CommandList>
							{isWaiting ? (
								<div
									className="flex items-center justify-center py-8"
									data-testid="popover-button-loading"
								>
									<Loader />
								</div>
							) : (
								sections.map((section, sectionIndex) => (
									<CommandGroup
										key={section.heading ?? `section-${sectionIndex}`}
										heading={section.heading}
										className={cn(
											GROUP_CLASS,
											sectionIndex === 0 && "pt-1.5",
											sectionIndex === sections.length - 1 && "pb-1.5",
										)}
									>
										{sectionIndex === 0 && resetOption && resetItem}
										{section.items.map(
											({ option: { label, value, submenu }, index }) =>
												submenu ? (
													<CommandSubmenu
														key={value}
														value={value}
														className={ITEM_CLASS}
														isOpen={openSubmenu === value}
														onOpenChange={(next) =>
															setOpenSubmenu(next ? value : null)
														}
														// Linear shows the caret here instead of a number.
														trigger={
															<span className="flex-1 truncate">{label}</span>
														}
													>
														{submenu.map((option) => (
															<CommandItem
																key={option.value}
																className={ITEM_CLASS}
																onSelect={() => select(option.value)}
															>
																<div className="flex w-full items-center gap-2">
																	<span className="flex-1 truncate">
																		{option.label}
																	</span>
																	{isSelected(option.value) && (
																		<Check className={CHECK_CLASS} />
																	)}
																</div>
															</CommandItem>
														))}
													</CommandSubmenu>
												) : (
													<CommandItem
														key={value}
														className={ITEM_CLASS}
														onSelect={() => select(value)}
													>
														<div className="flex w-full items-center gap-2">
															<span className="flex-1 truncate">{label}</span>
															{isSelected(value) && (
																<Check className={CHECK_CLASS} />
															)}
															{numberHint(index + resetOffset)}
														</div>
													</CommandItem>
												),
										)}
									</CommandGroup>
								))
							)}
						</CommandList>
					</Command>
				}
			>
				{children ? (
					children
				) : (
					<Button
						disabled={!hasChoices}
						variant={variant}
						className={cn("flex-shrink justify-start truncate", className, {
							"pointer-events-none": role === "VIEWER",
							"text-tertiary dark:text-secondary":
								showPlaceholder && !activePlaceholder,
						})}
						Icon={showPlaceholder && !!Icon && !isLoading ? Icon : undefined}
						iconClassName={activePlaceholder ? "text-primary" : undefined}
					>
						{/* min-w-0 down to the label lets it truncate when the chip has a max-w-*. */}
						<div className="flex min-w-0 flex-row items-center gap-1.5 [&>*:last-child]:min-w-0">
							{isLoading ? <Loader /> : null}
							{typeof triggerLabel === "string" ? (
								<span className="truncate">{triggerLabel}</span>
							) : (
								triggerLabel
							)}
						</div>
					</Button>
				)}
			</Popover>
		)
	},
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
